import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { get, send, type PracticeLog } from "./api.js";
import { PracticeInstrumentPicker, usePracticeInstrument } from "./practiceInstrument.js";

/** Only this explicit click starts practice; URLs remain harmless to reload. */
export function HomePracticeStart({ studentId, taskId }: { studentId: string; taskId: string }) {
  const choice = usePracticeInstrument(studentId);
  const navigate = useNavigate();
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const destination = `/practice?student=${encodeURIComponent(studentId)}`;
  async function start() {
    if (inFlight.current || choice.blocked || uncertain) return;
    inFlight.current = true;
    setBusy(true);
    setProblem(null);
    let dispatched = false;
    try {
      const base = `/me/students/${encodeURIComponent(studentId)}/practice`;
      const current = await get<PracticeLog>(base);
      if (!mounted.current) return;
      if (current.timer) {
        navigate(destination);
        return;
      }
      dispatched = true;
      await send("POST", `${base}/timer`, {
        practiceTodoId: taskId,
        ...(choice.value ? { instrument: choice.value } : {}),
      });
      if (mounted.current) navigate(destination);
    } catch (cause) {
      // A lost response must be reconciled in the existing timer workspace.
      if (!mounted.current) return;
      setUncertain(dispatched);
      setProblem(dispatched
        ? "Practice may have started. Open Practice to check before starting again."
        : cause instanceof Error ? cause.message : "Could not check your current practice timer.");
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  return <div className="stack">
    <PracticeInstrumentPicker choice={choice} disabled={busy} />
    <button type="button" className="action-secondary" disabled={busy || choice.blocked || uncertain} onClick={() => void start()}>
      {busy ? "Starting…" : "Start practice"}
    </button>
    {problem && <p className="small error" role="alert">{problem}</p>}
  </div>;
}
